import { DatacentralHookForm } from '@/components/ui/form-field'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    createCustomerMailer,
    getGroupMailerTypes,
    updateCustomerMailer,
} from '@/modules/Customer/services'
import { CreateCustomerMailer, CustomerMailer } from '@/modules/Customer/types'
import { getServiceByCustomerID } from '@/modules/Service/services'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'
import { schemaCreateCustomerMailerModal } from './schema'

export interface CustomerMailerModalProps extends ModalProps {
    customerID: number
    action?: 'create' | 'update'
    customerMailer?: CustomerMailer
    /** Pre-fill mailer_type_ids when creating from workflow steps */
    defaultMailerTypeIds?: number[]
    /** Pre-fill service_ids when creating from workflow steps */
    defaultServiceIds?: number[]
}

const CreateCustomerMailerModal = ({
    onClose,
    open,
    action = 'create',
    customerID,
    customerMailer,
    defaultMailerTypeIds,
    defaultServiceIds,
}: CustomerMailerModalProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'customers',
        mode: 'contains',
    })

    const { data: groupTypes, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.GROUP_MAILER_TYPES],
        queryFn: () => getGroupMailerTypes(),
    })

    const formControl = useForm<CreateCustomerMailer>({
        defaultValues: {
            customer_mailer_name: customerMailer?.customer_mailer_name,
            mailer_type_ids:
                customerMailer?.mailer_type_ids || defaultMailerTypeIds || [],
            mailer_receiver: customerMailer?.mailer_receiver || '',
            mailer_cc: customerMailer?.mailer_cc || '',
            mailer_bcc: customerMailer?.mailer_bcc || '',
            service_ids: customerMailer?.service_ids || defaultServiceIds || [],
            sendable: customerMailer ? customerMailer?.sendable : true,
        },
        resolver: yupResolver(schemaCreateCustomerMailerModal(t) as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn:
                action === 'update'
                    ? updateCustomerMailer
                    : createCustomerMailer,
        },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t(`${action}-customer-mailer-success`),
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const handleCreate = (values: CreateCustomerMailer) => {
        mutateAsync({
            customerID,
            customerMailerID: customerMailer?.customer_mailer_id as number,
            data: values,
        })
    }

    // Server-side service search for this customer
    const serviceAsyncOptions = useCallback(
        async (query: string) => {
            return getServiceByCustomerID({
                t,
                payload: { page: 1, page_size: 20, q: query },
                customerID,
            })
        },
        [t, customerID]
    )

    // Fetch default labels for pre-selected service_ids
    const serviceGetDefaultOptions = useCallback(async () => {
        return getServiceByCustomerID({
            t,
            payload: { page: 1, page_size: 1000 },
            customerID,
        })
    }, [t, customerID])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Dialog
            title={t(`customers.mailers.${action}`)}
            onClose={onClose}
            open={open}
        >
            <DatacentralHookForm<CreateCustomerMailer>
                formControl={formControl}
                onSubmit={handleCreate}
                columns={4}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outline',
                        handler: () => onClose(),
                        disabled: isPending,
                    },
                    {
                        title: t(`customers.mailers.${action}`),
                        type: 'submit',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            name: 'sendable',
                            type: 'checkbox',
                            label: t('sendable'),
                            bordered: true,
                        },
                        colSpan: 4,
                    },
                    {
                        formItemProps: {
                            name: 'customer_mailer_name',
                            type: 'textfield',
                            label: t('customer_mailer_name'),
                        },
                        colSpan: 4,
                    },
                    {
                        formItemProps: {
                            name: 'mailer_type_ids',
                            type: 'select',
                            multiple: true,
                            label: t('mailer_type_ids'),
                            placeholder: t('mailer_type_ids'),
                            options: groupTypes?.results?.map((groupType) => ({
                                value: groupType.mailer_type_id,
                                label: groupType.mailer_description,
                            })),
                        },
                        colSpan: 4,
                    },
                    {
                        formItemProps: {
                            name: 'mailer_receiver',
                            type: 'textfield',
                            label: t('mailer_receiver'),
                            placeholder: 'email@example.com',
                        },
                        colSpan: 4,
                    },
                    {
                        formItemProps: {
                            name: 'mailer_cc',
                            type: 'textfield',
                            label: t('mailer_cc'),
                            placeholder: 'cc@example.com',
                        },
                        colSpan: 2,
                    },
                    {
                        formItemProps: {
                            name: 'mailer_bcc',
                            type: 'textfield',
                            label: t('mailer_bcc'),
                            placeholder: 'bcc@example.com',
                        },
                        colSpan: 2,
                    },
                    {
                        formItemProps: {
                            name: 'service_ids',
                            type: 'select',
                            label: t('service_ids'),
                            multiple: true,
                            asyncOptions: serviceAsyncOptions,
                            getDefaultOptions: serviceGetDefaultOptions,
                            queryKey: `services-customer-${customerID}`,
                        },
                        colSpan: 4,
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateCustomerMailerModal
